import { EcommerceResponse, IEcommerceQuestionSchema, EcommerceBannerPlacement } from '../../types'
import style from './Main.module.scss'
import itemStyle from './ItemPreview.module.scss'
import ItemPreview from './ItemPreview'
import ItemsCarousel from '@aytm/app/swidget/base/questions/Ecommerce/components/ItemsCarousel.tsx'
import useViewport from '@aytm/app/swidget/base/modules/viewport/useViewport'
import useSurveyLocale from '@aytm/app/swidget/base/modules/locale/useSurveyLocale'
import { ViewportOrientation } from '@aytm/app/swidget/base/modules/viewport/types'
import SponsoredLabel from './SponsoredLabel'

interface ITrendingRowProps {
  schema: IEcommerceQuestionSchema
  response: EcommerceResponse
  ceilWidth?: number
  productsPerRow: number
  bannerPlacement: EcommerceBannerPlacement
  maxWidth?: number // Maximum width constraint for carousel
  filterKey?: 'featured' | 'banner' | 'footer' // Which products to show
  bannerSource?: string // Source product ID for footer placement
  onProductAction: (qnt: -1 | 1, index: string, subscribe?: number) => void
  onSpin: (event: React.KeyboardEvent, index: string, subscribe?: number) => void
  onItemSelect: (index: number, bannerPlacement?: string) => void
  saveScrollPoint: (event: React.MouseEvent) => void
}

const TrendingRow = ({
  schema,
  response,
  ceilWidth,
  productsPerRow,
  bannerPlacement,
  maxWidth,
  filterKey = 'featured',
  bannerSource,
  onProductAction,
  onSpin,
  onItemSelect,
  saveScrollPoint
}: ITrendingRowProps) => {
  const viewport = useViewport()
  const { t } = useSurveyLocale()
  const ITEM_MARGIN = 10

  // Filter products based on filterKey
  const trending =
    'footer' === filterKey
      ? schema.answers.filter(answer => answer.footer).filter(answer => !answer.na)
      : schema.answers.filter(answer => answer[filterKey]).filter(answer => !answer.na)
  const sponsoredSize = trending.length

  if (sponsoredSize === 0) return null

  const isProductPage = 'footer' === bannerPlacement
  const mobileItemWidth = viewport.mobile
    ? viewport.width * (ViewportOrientation.LANDSCAPE === viewport.orientation ? 0.3 : 0.6)
    : undefined

  return (
    <>
      <div className={`${style.results} ${isProductPage ? style.productResults : ''}`}>
        {t('questions', 'ec', 'amazon', isProductPage ? 'relatedProducts' : 'trending')}
        {!isProductPage && <br />}
        <div className={itemStyle.sponsoredBottom}>
          <SponsoredLabel />
        </div>
      </div>

      <ItemsCarousel itemsPerPage={productsPerRow} gap={ITEM_MARGIN} itemWidth={mobileItemWidth} maxWidth={maxWidth}>
        {trending.map((answer, index) => {
          const baseClassName = viewport.mobile ? itemStyle.vertical : ''
          const isDisabled = 'focus' == schema.mode_type && !answer.focused
          const itemClassName = `${baseClassName} ${isDisabled ? itemStyle.disabled : ''} ${isProductPage ? itemStyle.productPage : ''}`
          return (
            <ItemPreview
              key={index}
              schema={answer}
              questionSchema={schema}
              onProductAction={qnt => onProductAction(qnt, answer.id)}
              onSpin={event => onSpin(event, answer.id)}
              onItemSelect={() => onItemSelect(answer.index, bannerPlacement)}
              width={mobileItemWidth || ceilWidth}
              className={itemClassName}
              data-testid="SW-SH-Item"
              response={response.answers.find(a => a.id === answer.id && !a.subscribe)}
              index={answer.index}
              findability={'find' == schema.mode_type || (1 == schema.max_cart_size && !schema.shopping_cart)}
              saveScrollPoint={saveScrollPoint}
              bannerPlacement={bannerPlacement}
              bannerSource={bannerSource}
            />
          )
        })}
      </ItemsCarousel>
    </>
  )
}

export default TrendingRow
